import { Capacitor } from "@capacitor/core";
import {
  ConsentStatus,
  ConsentType,
  FirebaseAnalytics,
} from "@capacitor-firebase/analytics";
import { toAnalyticsRoute } from "@/lib/analyticsRoute";

export const ANALYTICS_CONSENT_STORAGE_KEY = "vs_privacy_consent_v1";
export const ANALYTICS_CONSENT_EVENT = "voltyshare:privacy-consent";
export const OPEN_ANALYTICS_SETTINGS_EVENT = "voltyshare:open-privacy-settings";

const GOOGLE_ANALYTICS_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || "G-VM6P0MVFFJ";
const GOOGLE_ADS_ID = "AW-18068209315";
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID?.trim() || "xmioyplih5";

export type AnalyticsConsent = {
  version: 1;
  analytics: boolean;
  heatmaps: boolean;
  marketing: boolean;
  updatedAt: string;
};

type GoogleTagCommand = (...args: unknown[]) => void;
type ClarityCommand = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GoogleTagCommand;
    clarity?: ClarityCommand & { q?: unknown[][] };
    __vsGoogleTagLoaded?: boolean;
    __vsGoogleAdsConfigured?: boolean;
    __vsClarityLoaded?: boolean;
  }
}

const blockedParameterPattern = /(^|_)(address|city|coordinates?|description|email|id|lat|latitude|lng|longitude|message|name|phone|plate|postal|query|referrer|text|token|user_agent)($|_)/i;
const allowedNamePattern = /^[a-z][a-z0-9_]{0,39}$/;

const ensureGtagQueue = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
};

const consentStatus = (allowed: boolean) => allowed ? "granted" : "denied";

const updateGoogleConsent = (consent: AnalyticsConsent | null) => {
  if (typeof window === "undefined") return;
  ensureGtagQueue();
  window.gtag?.("consent", "update", {
    analytics_storage: consentStatus(Boolean(consent?.analytics)),
    ad_storage: consentStatus(Boolean(consent?.marketing)),
    ad_user_data: consentStatus(Boolean(consent?.marketing)),
    ad_personalization: consentStatus(Boolean(consent?.marketing)),
  });
};

const loadGoogleTag = () => {
  if (typeof window === "undefined" || window.__vsGoogleTagLoaded) return;
  if (!GOOGLE_ANALYTICS_ID && !GOOGLE_ADS_ID) return;

  ensureGtagQueue();
  const tagId = GOOGLE_ANALYTICS_ID || GOOGLE_ADS_ID;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
  document.head.appendChild(script);
  window.__vsGoogleTagLoaded = true;
  window.gtag?.("js", new Date());

  if (GOOGLE_ANALYTICS_ID) {
    window.gtag?.("config", GOOGLE_ANALYTICS_ID, { send_page_view: false });
  }
};

const configureGoogleAds = (consent: AnalyticsConsent | null) => {
  if (!consent?.marketing || window.__vsGoogleAdsConfigured) return;
  window.gtag?.("config", GOOGLE_ADS_ID);
  window.__vsGoogleAdsConfigured = true;
};

const loadClarity = (consent: AnalyticsConsent) => {
  if (typeof window === "undefined" || !CLARITY_PROJECT_ID || window.__vsClarityLoaded) return;
  window.clarity = window.clarity || function clarity(...args: unknown[]) {
    (window.clarity!.q = window.clarity!.q || []).push(args);
  };
  window.clarity("consentv2", {
    ad_Storage: consentStatus(consent.marketing),
    analytics_Storage: consentStatus(consent.heatmaps),
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(CLARITY_PROJECT_ID)}`;
  document.head.appendChild(script);
  window.__vsClarityLoaded = true;
};

const toFirebaseConsentStatus = (allowed: boolean) => allowed
  ? ConsentStatus.Granted
  : ConsentStatus.Denied;

const applyNativeConsent = async (consent: AnalyticsConsent | null) => {
  const analyticsAllowed = Boolean(consent?.analytics);
  await Promise.all([
    FirebaseAnalytics.setConsent({
      type: ConsentType.AnalyticsStorage,
      status: toFirebaseConsentStatus(analyticsAllowed),
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdStorage,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdUserData,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdPersonalization,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setEnabled({ enabled: analyticsAllowed }),
  ]);
};

export const readAnalyticsConsent = (): AnalyticsConsent | null => {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) || "null");
    if (parsed?.version !== 1) return null;
    const native = Capacitor.isNativePlatform();
    return {
      version: 1,
      analytics: Boolean(parsed.analytics),
      heatmaps: native ? false : Boolean(parsed.analytics && parsed.heatmaps),
      marketing: native ? false : Boolean(parsed.marketing),
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date(0).toISOString(),
    };
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => Boolean(readAnalyticsConsent()?.analytics);

export const applyAnalyticsConsent = async (consent: AnalyticsConsent | null) => {
  if (typeof window === "undefined") return;
  updateGoogleConsent(consent);

  if (Capacitor.isNativePlatform()) {
    await applyNativeConsent(consent).catch(() => {});
    return;
  }

  if (consent?.analytics || consent?.marketing) {
    loadGoogleTag();
    configureGoogleAds(consent);
  }
  if (consent?.heatmaps) loadClarity(consent);
  if (window.__vsClarityLoaded) {
    window.clarity?.("consentv2", {
      ad_Storage: consentStatus(Boolean(consent?.marketing)),
      analytics_Storage: consentStatus(Boolean(consent?.heatmaps)),
    });
  }
};

export const saveAnalyticsConsent = async (
  next: Pick<AnalyticsConsent, "analytics" | "heatmaps" | "marketing">
) => {
  const native = Capacitor.isNativePlatform();
  const consent: AnalyticsConsent = {
    version: 1,
    analytics: native ? next.analytics : next.analytics || next.heatmaps,
    heatmaps: native ? false : next.heatmaps,
    marketing: native ? false : next.marketing,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  await applyAnalyticsConsent(consent);
  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: consent }));
  return consent;
};

export const initializeProductAnalytics = () => {
  if (typeof window === "undefined") return;
  ensureGtagQueue();
  window.gtag?.("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
  void applyAnalyticsConsent(readAnalyticsConsent());
};

const sanitizeParameterValue = (value: unknown): string | number | null => {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value.slice(0, 100);
  return null;
};

export const sanitizeAnalyticsParameters = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([key]) => allowedNamePattern.test(key) && !blockedParameterPattern.test(key))
      .map(([key, value]) => [key, sanitizeParameterValue(value)] as const)
      .filter((entry): entry is [string, string | number] => entry[1] !== null)
      .slice(0, 25)
  );

const externalEventAliases: Record<string, string> = {
  login_complete: "login",
  registration_complete: "sign_up",
  station_contribution_submit: "generate_lead",
};

export const toExternalAnalyticsEventName = (name: string) => externalEventAliases[name] || name;

export const trackProductEvent = async (
  name: string,
  params: Record<string, unknown> = {},
  _userId?: string
) => {
  if (!hasAnalyticsConsent() || !allowedNamePattern.test(name)) return;
  const externalName = toExternalAnalyticsEventName(name);
  const safeParams = sanitizeAnalyticsParameters(params);

  if (Capacitor.isNativePlatform()) {
    await FirebaseAnalytics.logEvent({ name: externalName, params: safeParams }).catch(() => {});
    return;
  }

  loadGoogleTag();
  if (!GOOGLE_ANALYTICS_ID) return;
  const pagePath = toAnalyticsRoute(window.location.pathname);
  const webParams = sanitizeAnalyticsParameters({
    ...params,
    page_path: pagePath,
  });
  window.gtag?.("event", externalName, {
    ...webParams,
    ...(externalName === "page_view" ? {
      page_location: `${window.location.origin}${pagePath}`,
      page_title: document.title.slice(0, 100),
    } : {}),
    ...(window.location.hostname === "127.0.0.1" && new URLSearchParams(window.location.search).has("codex_analytics_preview")
      ? { debug_mode: true }
      : {}),
  });
};

export const setProductAnalyticsScreen = async (screenName: string) => {
  if (!hasAnalyticsConsent()) return;
  const safeName = screenName.slice(0, 100);
  if (Capacitor.isNativePlatform()) {
    await FirebaseAnalytics.setCurrentScreen({ screenName: safeName }).catch(() => {});
  }
};
